import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const ListarProgramas = ({ refresh }) => {
  const [programas, setProgramas] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // 🗑️ Eliminar programa (memoizado)
  const deletePrograma = useCallback(async (id) => {
    try {
      const result = await Swal.fire({
        title: "¿Está seguro?",
        text: "¡No podrá revertir este proceso!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });

      if (result.isConfirmed) {
        const { data } = await axios.delete(`/api/programas/delete/${id}`);

        Swal.fire({
          icon: "success",
          title: data.message,
          showConfirmButton: false,
          timer: 1500,
        });
      }
    } catch (error) {
      console.error("Error al eliminar el programa:", error.message);
      Swal.fire("Error", "No se pudo eliminar el programa.", "error");
    }
  }, []);

  // 📥 Obtener programas
  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.get("/api/programas/listall/");
      const lista = data?.programas || [];

      const programasConFunciones = lista
        .map((programa) => ({
          ...programa,

          // 📚 Programa (control de ancho + ellipsis)
          nombrePrograma: (
            <div
              className="text-truncate"
              style={{ maxWidth: 260 }}
              title={programa.nombrePrograma}
            >
              {programa.nombrePrograma}
            </div>
          ),

          // ⚙️ Acciones (nuevo diseño)
          funciones: (
            <div className="d-flex gap-2 flex-wrap justify-content-end">


              {/* Registrar Guía 
              <button
                className="btn btn-dark btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() =>
                  navigate(
                    `/institucional/guias/nuevo/${programa.codPrograma}`
                  )
                }
                title="Registrar Guía del Programa"
                type="button"
              >
                <i className="fa-solid fa-file-circle-plus"></i>
              </button>
              */}

              {/* Listar Guías */}
              <button
                className="btn btn-outline-dark btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() =>
                  navigate(
                    `/institucional/guias/listarguias/${programa.codPrograma}`
                  )
                }
                title="Listar Guías del Programa"
                type="button"
              >
                <i className="fa-solid fa-list-check"></i>
              </button>

              {/* Editar */}
              <button
                className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() =>
                  navigate(`/institucional/programas/edit/${programa._id}`)
                }
                title="Editar Programa"
                type="button"
              >
                <i className="fa-solid fa-pen-to-square"></i>
              </button>

              {/* Eliminar */}
              <button
                className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={async () => {
                  await deletePrograma(programa._id);
                  getData();
                }}
                title="Eliminar Programa"
                type="button"
              >
                <i className="fa-solid fa-trash"></i>
              </button>
            </div>
          ),
        }))
        .reverse();

      setProgramas(programasConFunciones);
    } catch (error) {
      console.error("Error al cargar los programas:", error.message);
    } finally {
      setLoading(false);
    }
  }, [navigate, deletePrograma]);

  useEffect(() => {
    getData();
  }, [getData, refresh]);

  // 📊 Columnas (Programa con width controlado)
  const columns = useMemo(
    () => [
      { Header: "Código", accessor: "codPrograma" },
      {Header: "Programa",accessor: "nombrePrograma"},
      { Header: "Estado", accessor: "estadoPrograma" },
      { Header: "Nivel", accessor: "nivelPrograma" },
      { accessor: "funciones" },
    ],
    []
  );

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Programas de Formación
                </h5>
                <small className="text-secondary">
                  Gestión institucional y administración de programas.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  disabled={loading}
                  title="Actualizar"
                >
                  <i
                    className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-rotate"
                      } me-2`}
                  ></i>
                  Actualizar
                </button>

                <button
                  className="btn btn-dark"
                  type="button"
                  onClick={() => navigate("/institucional/programas/nuevo")}
                  title="Crear Programa"
                >
                  <i className="fa-solid fa-plus me-2"></i>
                  Nuevo
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div className="spinner-border" role="status" />
                <div className="mt-3">Cargando programas…</div>
              </div>
            ) : Array.isArray(programas) && programas.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={programas} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">📚</div>
                <div className="fw-semibold">
                  No hay programas de formación registrados
                </div>
                <div className="small mb-3">
                  Crea el primer programa para iniciar la gestión.
                </div>
                <button
                  className="btn btn-dark"
                  type="button"
                  onClick={() => navigate("/institucional/programas")}
                >
                  <i className="fa-solid fa-plus me-2"></i>
                  Crear programa
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarProgramas;